import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import api, { getCurrentUser } from '../../services/api';
import { formatCents } from '../../utils/money';

/**
 * Whether the paid features (From Topic, From Script, Speech Generator) are
 * locked for this user: a balance at or below the server's threshold. Both
 * values come from the server, which enforces the same rule on submit — this
 * only stops a user filling in a form that would be refused.
 *
 * `locked` stays false until both are known, so nothing flashes locked.
 */
export const usePaidFeatureLock = () => {
  const [balance, setBalance] = useState(null);
  const [minBalance, setMinBalance] = useState(null);
  const [aiAvatarPaid, setAiAvatarPaid] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/stock-video/catalogs')
      .then((r) => { if (!cancelled) setMinBalance(r?.paid_feature_min_balance_cents ?? null); })
      .catch((err) => console.error('Could not load the paid-feature threshold:', err));
    getCurrentUser()
      .then((user) => {
        if (cancelled) return;
        setBalance(user?.credits ?? null);
        setAiAvatarPaid(user?.ai_avatar_paid ?? null);
      })
      .catch((err) => console.error('Could not load balance:', err));
    return () => { cancelled = true; };
  }, []);

  return {
    locked: balance != null && minBalance != null && balance <= minBalance,
    // A subscriber stays unlocked on a low balance, so this is the server's flag and not `locked`.
    aiAvatarLocked: aiAvatarPaid === false,
    minBalance,
  };
};

export const PaidBadge = () => (
  <span className="rounded-full bg-volt px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-ink shadow">
    Paid
  </span>
);

export const PaidLockIcon = ({ className = 'h-4 w-4' }) => <Lock className={`${className} text-volt`} />;

/** One line explaining the lock, with a way to lift it. */
export const PaidFeatureNote = ({ what, plural = false, minBalance, className = '' }) => (
  <p className={`font-body text-xs text-muteDim ${className}`}>
    {what} {plural ? 'are paid features. They unlock' : 'is a paid feature. It unlocks'} once your
    balance is above {formatCents(minBalance)}. From Audio works on any balance.{' '}
    <Link to="/credits" className="text-volt hover:underline">Add balance</Link>
  </p>
);

export const AiAvatarPaidNote = ({ minBalance, className = '' }) => (
  <p className={`font-body text-xs text-muteDim ${className}`}>
    AI Avatar is a paid feature. It unlocks with any subscription, or once your balance is above{' '}
    {formatCents(minBalance)}.{' '}
    <Link to="/credits" className="text-volt hover:underline">Add balance</Link>
  </p>
);
